import React, { useState } from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import ScriptImport from './ScriptImport';
import ScriptRundown from './ScriptRundown';
import ScriptFollowingCard from './ScriptFollowingCard';
import { useScriptOperations } from './scriptOperations';

const noop = () => {};
const flagOn = () => process.env.REACT_APP_SCRIPT_FOLLOWING_ENABLED === 'true';

/** Tenant-level library (Settings → Scripts). Hidden unless the feature flag is on. */
export function ScriptLibraryFeature({ enabled = flagOn(), ...props }) {
 return enabled ? <ScriptLibraryPanel {...props} /> : null;
}

/** Per-channel rundown and live status (the channel page). Hidden unless the feature flag is on. */
export function ScriptFollowingFeature({ enabled = flagOn(), ...props }) {
 return enabled ? <ScriptFollowingPanel {...props} /> : null;
}

/**
 * The script library belongs to the tenant, not to a channel: importing here never activates
 * anything and never changes a live channel. `channelid` only keys import drafts.
 */
export default function ScriptLibraryPanel({ restreamer, channelid = 'library', onCommitted = noop }) {
 const operations = useScriptOperations(restreamer, channelid);
 return <Paper sx={{ p: 2 }}>
  <Typography variant="body2">Script library — shared by every channel. Importing does not activate following or change a live channel.</Typography>
  <ScriptImport channelId={channelid} {...operations} onCommitted={onCommitted} />
 </Paper>;
}

/** One channel's script following: where it is now, how to start and stop it, and what is queued. */
export function ScriptFollowingPanel({ restreamer, channelid }) {
 const operations = useScriptOperations(restreamer, channelid);
 const [live, setLive] = useState(false);
 const [importing, setImporting] = useState(false);
 const [added, setAdded] = useState(null);
 return <Stack spacing={1}>
  <ScriptFollowingCard channelId={channelid} getFollowing={operations.getFollowing} getCaptionsState={operations.getCaptionsState} activate={operations.activate}
                       deactivate={operations.deactivate} control={operations.control}
                       onStatus={status => setLive(status?.proxy === 'provisioned')} />
  <Paper sx={{ p: 2 }}>
   <Stack spacing={1.5}>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
     <Button variant="outlined" size="small" onClick={() => setImporting(open => !open)}>
      {importing ? 'Close import' : 'Import a script'}
     </Button>
     <Typography variant="body2">A .txt, .vtt or .srt file. It is saved to your script library and can be added to any channel's rundown.</Typography>
    </Stack>
    {importing && <ScriptImport channelId={channelid} {...operations}
                                onCommitted={version => { setAdded(previous => ({ ...version, token: (previous?.token || 0) + 1 })); setImporting(false); }} />}
    <ScriptRundown channelId={channelid} active={live} listScripts={operations.listScriptVersions}
                   getRundown={operations.getRundown} saveRundown={operations.saveRundown} scriptAdded={added} />
   </Stack>
  </Paper>
 </Stack>;
}
